<template>
    <div>
        <overlap-display-modal
            ref="overlapDisplayModal"
            :groupOverlap="groupOverlap"
        />
        <div class="header">
            <div class="text-header">
                {{ headerText }}
            </div>
            <div class="action-header">
                <v-btn
                    @click="cancel()"
                    outlined
                    class="mr-2"
                    width="180"
                    color="primary">
                    {{ $vuetify.lang.t('$vuetify.groupWorkdayForm.cancel') }}
                </v-btn>

                <v-btn
                    v-if="canSaveOrUpdate"
                    @click="save()"
                    width="180"
                    color="primary">
                    {{ $vuetify.lang.t('$vuetify.groupWorkdayForm.save') }}
                </v-btn>
            </div>
        </div>
        <v-form
            lazy-validation
            ref="form"
            v-model="valid">
            <v-card
                class="card-container-header"
            >
                <div class="row">
                    <div class="col-3">
                        <span class="locale-font">
                            {{ $vuetify.lang.t('$vuetify.groupWorkdayForm.groupName') }}
                        </span>
                        <v-text-field
                            v-model="form.name"
                            autocomplete="off"
                            class="group-name-input"
                            :rules="inputRules"
                            :error-messages="errors.name"
                            :placeholder="$vuetify.lang.t('$vuetify.groupWorkdayForm.enterGroupName')">
                        </v-text-field>
                    </div>
                    <div class="col-5">
                        <div v-if="isFeatureLineCheckIn === 'true'">
                            <span class="locale-font"> {{ $vuetify.lang.t('$vuetify.groupWorkdayForm.checkInOption') }}</span>
                            <div class="d-flex">
                                <v-switch
                                    class="mr-6"
                                    :label="$vuetify.lang.t('$vuetify.groupWorkdayForm.checkInByLine')"
                                    color="primary"
                                    v-model="form.isCheckInByLine">
                                </v-switch>
                                <v-switch
                                    :label="$vuetify.lang.t('$vuetify.groupWorkdayForm.checkInAnyWhere')"
                                    color="primary"
                                    v-model="form.isCheckInAnywhere">
                                </v-switch>
                            </div>
                        </div>
                    </div>
                    <div class="col-4 mb-6 text-box">
                        <i class="fas fa-lightbulb-on"> {{$vuetify.lang.t('$vuetify.groupWorkdayForm.information')}}</i>
                        <span>
                            <ul>
                                <li class="icon-text">{{$vuetify.lang.t('$vuetify.groupWorkdayForm.workingTimeEachDay')}}</li>
                                <li class="icon-text">{{$vuetify.lang.t('$vuetify.groupWorkdayForm.limitsWorkingHours')}}</li>
                                <li class="icon-text">{{$vuetify.lang.t('$vuetify.groupWorkdayForm.maximumLimitsOvertime')}}</li>
                            </ul>
                        </span>
                    </div>
                </div>
                <div class="column-head locale-font">
                    <div>
                        <div
                            class="column-item"
                            v-for="item in header"
                            :key="item">
                            {{ item }}
                        </div>
                    </div>
                </div>
            </v-card>
            <v-card
                v-if="showWorkday"
                class="card-container-content">
                <div class="column-head"
                    v-for="(item, index) in form.workdayData"
                    :key="item.day">
                    <div>
                        <div class="column-item">
                            {{ days[item.dayIndex] }}
                        </div>
                        <div class="column-item">
                            <v-text-field
                                v-model="item.checkinTime"
                                autocomplete="off"
                                type="time"
                                :error-messages="errors.checkInTime[index]"
                                :disabled="item.dayIndex === todayIndex"
                                placeholder=" ">
                            </v-text-field>
                        </div>
                        <div class="column-item">
                            <v-text-field
                                v-model="item.startTime"
                                autocomplete="off"
                                type="time"
                                :error-messages="errors.startTime[index]"
                                :disabled="item.dayIndex === todayIndex"
                                placeholder=" ">
                            </v-text-field>
                        </div>
                        <div class="column-item">
                            <hours-input
                                v-model="item.breakhours"
                                :default-value="''"
                                :disabled="item.dayIndex === todayIndex"
                                :error-messages="errors.breakhours[index]"
                            />
                        </div>
                        <div class="column-item">
                            <v-text-field
                                v-model="item.endTime"
                                autocomplete="off"
                                type="time"
                                :disabled="item.dayIndex === todayIndex"
                                :error-messages="errors.endTime[index]"
                                placeholder=" ">
                            </v-text-field>
                        </div>
                        <div class="column-item">
                            <v-text-field
                                v-model="item.totalWorkHours"
                                autocomplete="off"
                                append-icon="mdi-timer"
                                v-mask="'##:##:##'"
                                :disabled="item.dayIndex === todayIndex"
                                :error-messages="errors.totalWorkTime[index]"
                                readonly>
                            </v-text-field>
                        </div>
                        <div
                            class="column-item"
                            :style="{ 'pointer-events': item.dayIndex === todayIndex ? 'none' : null }">
                            <toggle-status
                                v-model="item.isWorkday"
                                :disabled="item.dayIndex === todayIndex"
                            />
                        </div>
                    </div>
                    <div class="row-item" v-if="item.dayIndex === todayIndex">
                        <v-icon
                            color="#6c98ba"
                            size="14">
                            information
                        </v-icon>
                        <span>{{ $vuetify.lang.t('$vuetify.groupWorkdayForm.msgCannotModify') }}</span>
                    </div>
                </div>
            </v-card>
        </v-form>
    </div>
</template>

<script>
import ToggleStatus from '@/components/ToggleStatus.vue'
import HoursInput from '@/components/HoursInput'
import OverlapDisplayModal from '@/components/modal/OverlapDisplayModal.vue'
import { mask } from 'vue-the-mask'

export default {
    name: 'GroupWorkdayForm',

    components: {
        ToggleStatus,
        HoursInput,
        OverlapDisplayModal
    },

    directives: {
        mask
    },
    computed: {
        header () {
            return [
                this.$vuetify.lang.t('$vuetify.groupWorkdayForm.day'),
                this.$vuetify.lang.t('$vuetify.groupWorkdayForm.checkInTime'),
                this.$vuetify.lang.t('$vuetify.groupWorkdayForm.startTime'),
                this.$vuetify.lang.t('$vuetify.groupWorkdayForm.breakHours'),
                this.$vuetify.lang.t('$vuetify.groupWorkdayForm.endTime'),
                this.$vuetify.lang.t('$vuetify.groupWorkdayForm.totalWorkHours'),
                this.$vuetify.lang.t('$vuetify.groupWorkdayForm.status')
            ]
        },
        days () {
            return [
                this.$vuetify.lang.t('$vuetify.groupWorkdayForm.days.monday'),
                this.$vuetify.lang.t('$vuetify.groupWorkdayForm.days.tuesday'),
                this.$vuetify.lang.t('$vuetify.groupWorkdayForm.days.wednesday'),
                this.$vuetify.lang.t('$vuetify.groupWorkdayForm.days.thursday'),
                this.$vuetify.lang.t('$vuetify.groupWorkdayForm.days.friday'),
                this.$vuetify.lang.t('$vuetify.groupWorkdayForm.days.saturday'),
                this.$vuetify.lang.t('$vuetify.groupWorkdayForm.days.sunday')
            ]
        },
        headerText () {
            if (this.mode === 'create') {
                return this.$vuetify.lang.t('$vuetify.groupWorkdayForm.createGroup')
            } else {
                return `${this.$vuetify.lang.t('$vuetify.groupWorkdayForm.workday')} - ${this.groupName}`
            }
        },
        errorMsg () {
            return this.$vuetify.lang.t('$vuetify.groupWorkdayForm.msgError')
        },
        saveSuccess () {
            let modeText = this.$vuetify.lang.t('$vuetify.groupWorkdayForm.msgCreateSuccess')
            if (this.mode === 'update') {
                modeText = this.$vuetify.lang.t('$vuetify.groupWorkdayForm.msgUpdateSuccess')
            }
            return {
                title: this.$vuetify.lang.t('$vuetify.groupWorkdayForm.congratulation'),
                text: modeText,
                type: 'success'
            }
        },
        msgRequiredField () {
            return this.$vuetify.lang.t('$vuetify.groupWorkdayForm.msgRequiredField')
        },
        msgCheckInOverlapStartTime () {
            return this.$vuetify.lang.t('$vuetify.groupWorkdayForm.checkInTimeOverlapStartTime')
        },
        msgStartTimeOverlapCheckIn () {
            return this.$vuetify.lang.t('$vuetify.groupWorkdayForm.startTimeOverlapCheckInTime')
        },
        msgCheckInOverlapEndTime () {
            return this.$vuetify.lang.t('$vuetify.groupWorkdayForm.checkInTimeOverlapEndTime')
        },
        msgEndTimeOverlaoCheckIn () {
            return this.$vuetify.lang.t('$vuetify.groupWorkdayForm.checkInTimeOverlapEndTime')
        },
        msgCheckInShouldAfterEndTime () {
            return this.$vuetify.lang.t('$vuetify.groupWorkdayForm.checkInShouldAfterEndTime')
        },
        msgBreakhoursIsRequired () {
            return this.$vuetify.lang.t('$vuetify.groupWorkdayForm.breakhoursIsRequired')
        },
        msgIncorrectWorkTimePeriod () {
            return this.$vuetify.lang.t('$vuetify.groupWorkdayForm.incorrectWorkTimePeriod')
        }
    },
    data () {
        return {
            todayIndex: null,
            form: {
                name: '',
                branchId: null,
                isCheckInByLine: false,
                isCheckInAnywhere: false,
                workdayData: [
                    {
                        dayIndex: 0,
                        checkinTime: '05:00',
                        startTime: '08:00',
                        breakhours: '01:00',
                        endTime: '18:00',
                        totalWorkHours: '09:00',
                        isWorkday: true
                    },
                    {
                        dayIndex: 1,
                        checkinTime: '05:00',
                        startTime: '08:00',
                        breakhours: '01:00',
                        endTime: '18:00',
                        totalWorkHours: '09:00',
                        isWorkday: true
                    },
                    {
                        dayIndex: 2,
                        checkinTime: '05:00',
                        startTime: '08:00',
                        breakhours: '01:00',
                        endTime: '18:00',
                        totalWorkHours: '09:00',
                        isWorkday: true
                    },
                    {
                        dayIndex: 3,
                        checkinTime: '05:00',
                        startTime: '08:00',
                        breakhours: '01:00',
                        endTime: '18:00',
                        totalWorkHours: '09:00',
                        isWorkday: true
                    },
                    {
                        dayIndex: 4,
                        checkinTime: '05:00',
                        startTime: '08:00',
                        breakhours: '01:00',
                        endTime: '18:00',
                        totalWorkHours: '09:00',
                        isWorkday: true
                    },
                    {
                        dayIndex: 5,
                        checkinTime: '05:00',
                        startTime: '08:00',
                        breakhours: '01:00',
                        endTime: '18:00',
                        totalWorkHours: '09:00',
                        isWorkday: false
                    },
                    {
                        dayIndex: 6,
                        checkinTime: '05:00',
                        startTime: '08:00',
                        breakhours: '01:00',
                        endTime: '18:00',
                        totalWorkHours: '09:00',
                        isWorkday: false
                    }
                ]
            },
            errors: {
                name: null,
                breakhours: [],
                checkInTime: [],
                endTime: [],
                startTime: [],
                totalWorkTime: [],
                dayIndex: []
            },
            valid: true,
            inputRules: [
                v => this.$validator.hasValue(v, this.$vuetify.lang.t('$vuetify.groupWorkdayForm.msgRequiredField')),
                v => this.$validator.lengthNotExceed(v, 255, this.$vuetify.lang.t('$vuetify.groupWorkdayForm.msgExceededField'))
            ],
            canSaveOrUpdate: false,
            groupName: '',
            mode: '',
            showWorkday: false,
            groupOverlap: {},
            isFeatureLineCheckIn: process.env.VUE_APP_CHECK_IN_LOCATION
        }
    },

    methods: {
        isCheckInTimeOverlapStartTime (index) {
            let isOverlap = false
            if (this.form.workdayData[index].isWorkday) {
                if (this.form.workdayData[index].checkinTime > this.form.workdayData[index].startTime) {
                    isOverlap = true
                }
            }

            this.errors.checkInTime[index] = isOverlap ? `${this.msgCheckInOverlapStartTime} ${this.form.workdayData[index].checkinTime}.` : null
            this.errors.startTime[index] = isOverlap ? `${this.msgStartTimeOverlapCheckIn} ${this.form.workdayData[index].startTime}.` : null

            return isOverlap
        },

        isCheckInTimeOvrelapEndTime (index) {
            let isOverlap = false
            if (!this.form.workdayData[index].isWorkday) {
                if (this.form.workdayData[index].checkinTime > this.form.workdayData[index].endTime) {
                    isOverlap = true
                }
            }

            this.errors.endTime[index] = isOverlap ? `${this.msgCheckInOverlapEndTime} ${this.form.workdayData[index].endTime}.` : null
            this.errors.checkInTime[index] = isOverlap ? `${this.msgEndTimeOverlaoCheckIn}  ${this.form.workdayData[index].checkinTime}.` : null

            return isOverlap
        },

        getNextDayIndex (index) {
            const indexNextDay = index + 1
            return (indexNextDay > 6) ? 0 : indexNextDay
        },

        getPreviousDayIndex (index) {
            const indexPrevDay = index - 1
            return (indexPrevDay < 0) ? 6 : indexPrevDay
        },

        converteStrToMinute (data) {
            let result = 0

            const [hourStr, minuteStr, secondStr] = data.split(':')
            if (data.split(':').length < 2) {
                return 0
            }
            const hourInMinute = parseInt(hourStr) * 60
            const minute = parseInt(minuteStr)
            let secondInMinute = (parseInt(secondStr / 1000))

            if (!secondInMinute) {
                secondInMinute = 0
            }

            result = hourInMinute + minute + secondInMinute

            return result
        },

        isEndTimeOverlapNextDayCheckIn (index) {
            const nextDayIndex = this.getNextDayIndex(index)

            // when next day is current day, cannot chack
            if (this.todayIndex === nextDayIndex) {
                return false
            }

            if (
                !this.form.workdayData[nextDayIndex].isWorkday ||
                !this.form.workdayData[index].isWorkday
            ) {
                return false
            }

            const startTimeStr = this.form.workdayData[index].startTime
            const endTimeStr = this.form.workdayData[index].endTime
            const checkInNextDayTimeStr = this.form.workdayData[nextDayIndex].checkinTime

            const isOverlap = this.checkTimeOverlap(startTimeStr, endTimeStr, checkInNextDayTimeStr)
            this.errors.endTime[index] = isOverlap ? 'เวลา end time ต้องน้อยกว่า start check in ของวันถัดไป' : null
            this.errors.checkInTime[nextDayIndex] = isOverlap ? this.msgCheckInShouldAfterEndTime : null

            return isOverlap
        },

        isCheckInTimeOverlapPreviousDayEnd (index) {
            const prevDayIndex = this.getPreviousDayIndex(index)

            // when index and prevDay is current day , cannot chack
            if (this.todayIndex === index || this.todayIndex === prevDayIndex) {
                return false
            }

            if (
                !this.form.workdayData[prevDayIndex].isWorkday ||
                !this.form.workdayData[index].isWorkday
            ) {
                return false
            }

            const startTimeStr = this.form.workdayData[prevDayIndex].startTime
            const endTimeStr = this.form.workdayData[prevDayIndex].endTime
            const checkInNextDayTimeStr = this.form.workdayData[index].checkinTime

            const isOverlap = this.checkTimeOverlap(startTimeStr, endTimeStr, checkInNextDayTimeStr)
            this.errors.checkInTime[index] = isOverlap ? this.msgCheckInShouldAfterEndTime : null
            this.errors.endTime[prevDayIndex] = isOverlap ? 'เวลา end time ต้องน้อยกว่า start check in ของวันถัดไป' : null

            return isOverlap
        },

        checkTimeOverlap (startTimeStr, endTimeStr, checkInNextDayTimeStr) {
            const startTime = this.converteStrToMinute(startTimeStr)
            let endTime = this.converteStrToMinute(endTimeStr)
            const nextTime = this.converteStrToMinute(checkInNextDayTimeStr)

            if (endTime > startTime) {
                // 1440 is 24 hours
                const minutesInOneDay = 60 * 24
                endTime = endTime - minutesInOneDay
            }

            if (endTime < nextTime) {
                return false
            }

            return true
        },

        checkError (error) {
            // 7 is 7 day for check error
            for (let i = 0; i < 7; i++) {
                if (error.breakhours[i]) {
                    return true
                }
                if (error.checkInTime[i]) {
                    return true
                }
                if (error.endTime[i]) {
                    return true
                }
                if (error.startTime[i]) {
                    return true
                }
                if (error.totalWorkTime[i]) {
                    return true
                }
            }

            return false
        },

        async save () {
            if (!this.$refs.form.validate()) {
                return
            }

            const isErrors = this.checkError(this.errors)

            if (isErrors) {
                return
            }

            this.form.name = this.form.name.trim()
            // this.addMissingSecondsInInputTime()
            if (this.mode === 'create') {
                await this.create()
            } else if (this.mode === 'update') {
                await this.update()
            }
        },

        addMissingSecondsInInputTime () {
            const { workdayData } = this.form

            for (let i = 0; i < workdayData.length; i++) {
                const { checkinTime, startTime, endTime } = workdayData[i]
                // after split by ':' if length of values is not 3 that mean time is missing second
                if (checkinTime.split(':').length !== 3) {
                    this.form.workdayData[i].checkinTime = `${checkinTime}:00`
                }
                if (startTime.split(':').length !== 3) {
                    this.form.workdayData[i].startTime = `${startTime}:00`
                }
                if (endTime.split(':').length !== 3) {
                    this.form.workdayData[i].endTime = `${endTime}:00`
                }
            }
        },

        async create () {
            this.$store.dispatch('setLoading', true)
            const res = await this.axios.post('group/workdays/create', this.form)
            if (res && res.data && res.data.status === 'ok') {
                this.$store.dispatch('setLoading', false)
                this.$notify(this.saveSuccess)
                this.$router.push({ name: 'TimeTableSetting' }, () => {})
            } else if (res && res.data && res.data.status === 'error_inputs') {
                if ('breakhours' in res.data.errors) {
                    this.errors = res.data.errors
                } else {
                    this.errors.breakhours = []
                    this.errors.name = res.data.errors.name
                }
                if ('totalWorkTime' in res.data.errors) {
                    this.errors.totalWorkTime = res.data.errors.totalWorkTime
                }
                this.$store.dispatch('setLoading', false)
            }
        },

        async update () {
            this.$store.dispatch('setLoading', true)
            const groupId = this.$route.params.id

            const res = await this.axios.post(`group/workdays/edit/${groupId}`, this.form)
            if (res && res.data && res.data.status === 'ok') {
                this.$store.dispatch('setLoading', false)
                this.$notify(this.saveSuccess)
                this.$router.push({ name: 'TimeTableSetting' }, () => {})
            } else if (res && res.data && res.data.status === 'error_inputs') {
                if ('breakhours' in res.data.errors) {
                    this.errors = res.data.errors
                } else {
                    this.errors.breakhours = []
                    this.errors.name = res.data.errors.name
                }
                if ('checkinTime' in res.data.errors) {
                    this.errors.checkInTime = res.data.errors.checkInTime
                }
                if ('endTime' in res.data.errors) {
                    this.errors.endTime = res.data.errors.endTime
                }
                if ('totalWorkTime' in res.data.errors) {
                    this.errors.totalWorkTime = res.data.errors.totalWorkTime
                }
                if ('dayIndex' in res.data.errors) {
                    this.errors.dayIndex = res.data.errors.dayIndex
                }

                for (const property in this.errors) {
                    if (property !== 'name' && this.errors[property] && this.errors[property].length > 0) {
                        for (let i = 0; i < this.errors[property].length; i++) {
                            if (this.errors[property][i] && this.errors[property][i] !== null) {
                                this.$notify({
                                    title: this.errorMsg,
                                    text: this.errors[property][i],
                                    type: 'error'
                                })
                            }
                        }
                    }
                }
                this.$store.dispatch('setLoading', false)
            } else if (res && res.data && res.data.status === 'error_overlap') {
                this.groupOverlap = res.data.errors
                this.$refs.overlapDisplayModal.dialog = true
                for (const property in this.groupOverlap) {
                    this.$notify({
                        title: this.$vuetify.lang.t('$vuetify.groupWorkdayForm.error'),
                        text: this.$vuetify.lang.t(`$vuetify.groupWorkdayForm.${property}`),
                        type: 'error'
                    })
                }
                this.$store.dispatch('setLoading', false)
            }
        },

        cancel () {
            this.$router.push({ name: 'TimeTableSetting' }, () => {})
        },

        async getBranch () {
            this.form.branchId = localStorage.getItem('currentBranchId')

            if (this.form.branchId) {
                this.canSaveOrUpdate = true
            }
        },

        async getGroupWorkday (groupId) {
            this.$store.dispatch('setLoading', true)
            const res = await this.axios.get(`group/workdays/${groupId}`)

            if (res && res.data && res.data.status === 'ok') {
                const data = res.data.data
                this.form.workdayData = data.groupWorkday
                this.groupName = data.group.name
                this.form.isCheckInByLine = data.group.isCheckInByLine
                this.form.isCheckInAnywhere = data.group.isCheckInAnywhere
                this.form.name = data.group.name
                this.todayIndex = data.todayIndex

                this.showWorkday = true
                this.$store.dispatch('setLoading', false)
            }
        },

        async checkCreateOrEdit () {
            const groupId = this.$route.params.id
            if (groupId) {
                await this.getGroupWorkday(groupId)

                this.mode = 'update'
            } else {
                this.mode = 'create'
                this.showWorkday = true
            }
        },

        converteStrToDateTime (data) {
            const date = new Date()
            const [hoursStr, minuteStr, secondStr] = data.split(':')

            date.setHours(parseInt(hoursStr))
            date.setMinutes(parseInt(minuteStr))
            if (secondStr) {
                date.setSeconds(parseInt(secondStr))
            } else {
                date.setSeconds(0)
            }

            return date
        },

        converteTimeToStr (hours, minutes, seconds) {
            if (hours < 0 || !hours) {
                hours = 0
            }
            if (minutes < 0 || !minutes) {
                minutes = 0
            }
            if (seconds < 0 || !seconds) {
                seconds = 0
            }

            // return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
        },

        calTotalWorkHours (index) {
            const startTimeStr = this.form.workdayData[index].startTime
            const endTimeStr = this.form.workdayData[index].endTime
            const [hoursBreakStr, minuteBreakStr] = this.form.workdayData[index].breakhours.split(':')

            this.errors.breakhours[index] = (hoursBreakStr === '') ? this.msgBreakhoursIsRequired : null

            const dateTimeStart = this.converteStrToDateTime(startTimeStr)
            const dateTimeEnd = this.converteStrToDateTime(endTimeStr)

            // hrs to ms
            const hoursBreak = parseInt(hoursBreakStr) * (1000 * 60 * 60)
            // m to ms
            const minutesBreak = parseInt(minuteBreakStr) * (1000 * 60)

            if (dateTimeEnd.getHours() <= dateTimeStart.getHours()) {
                const nextDay = dateTimeEnd.getDate() + 1
                dateTimeEnd.setDate(nextDay)
            }

            // date time end and date time start is ms
            const totalMilliseconds = dateTimeEnd.getTime() - dateTimeStart.getTime() - (hoursBreak + minutesBreak)

            // ms to hrs
            const hours = Math.floor(((totalMilliseconds) / (1000 * 60 * 60)) % 24)
            // ms to m
            const minutes = Math.floor(((totalMilliseconds) / (1000 * 60)) % 60)
            // ms to s
            const seconds = Math.floor(((totalMilliseconds) / 1000) % 60)

            const timeStr = this.converteTimeToStr(hours, minutes, seconds)
            this.form.workdayData[index].totalWorkHours = timeStr

            // this.errors.totalWorkTime[index] = (timeStr === '00:00:00') ? this.msgIncorrectWorkTimePeriod : null
            this.errors.totalWorkTime[index] = (timeStr === '00:00') ? this.msgIncorrectWorkTimePeriod : null
        }
    },

    async mounted () {
        await this.getBranch()
        await this.checkCreateOrEdit()
    },
    watch: {
        '$vuetify.lang.current': {
            handler: function (after, before) {
                this.$util.recheckValidate(this.$refs.form.inputs)
            },
            deep: true
        },
        'form.workdayData': {
            handler: function (v) {
                for (let i = 0; i < v.length; i++) {
                    this.calTotalWorkHours(i)
                    if (this.isCheckInTimeOverlapStartTime(i)) {
                        break
                    } else if (this.isCheckInTimeOvrelapEndTime(i)) {
                        break
                    } else if (this.isEndTimeOverlapNextDayCheckIn(i)) {
                        break
                    } else if (this.isCheckInTimeOverlapPreviousDayEnd(i)) {
                        break
                    }
                }
            },
            deep: true
        }
    }

}
</script>

<style lang="scss" scoped>
.header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 2rem;
}
.card-container-header {
    padding: 2rem 5rem 0.5rem;
    margin-bottom: 0.05rem;
    box-shadow: 0 5px 25px 0 rgba(0, 0, 0, 0.05);
    border-radius: 15px 15px 0px 0px !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.25);
}
.card-container-content {
    padding: 0 5rem 2rem;
    margin-bottom: 0.05rem;
    box-shadow: 0 5px 25px 0 rgba(0, 0, 0, 0.05);
    border-radius: 0px 0px 15px 15px !important;
}
.column-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    & > div {
        width: 100%;
        display: flex;
        align-items: center;
    }

    .column-item {
        width: 20%;
        padding-right: 16px;
        box-sizing: border-box;
    }
    .column-item:last-child {
        width: 25%;
        text-align: center;
    }
    .column-item:first-child {
        width: 15%;
    }

    .row-item {
        width: 100%;
        padding-left: 15%;
        display: flex;
        font-size: 14px;
        color: #6c98ba;
        margin-top: -15px;

        span {
            margin-left: -95px;
        }
    }
}
.group-name-input {
    width: 200px;
}

.icon-text {
    font-size: 12px;
    margin: 7px 0px 8px 6px;
}

.text-box {
    box-shadow: 0 0 1px 0;
}

::v-deep .fas {
    font-family: "Font Awesome 5 Pro";
    font-weight: 900;
    padding-left: 10px;
    color: #d76436;
}

@media only screen and (max-width: 1515px) {
    .card-container-header {
        font-size: 13px;
    }
    .card-container-content {
        font-size: 13px;
    }
    ::v-deep .v-input input {
        max-height: 20px;
        font-size: 13px;
    }
    .icon-text {
        font-size: 10px;
    }
}
@media only screen and (max-width: 1415px) {
    .card-container-header {
        font-size: 11px;
    }
    .card-container-content {
        font-size: 11px;
    }
    ::v-deep .v-input input {
        max-height: 20px;
        font-size: 11px;
    }
    .column-head {
        .column-item {
            width: 13%;
            padding-right: 16px;
            box-sizing: border-box;
        }
        .column-item:first-child {
            width: 10%;
        }
    }
    .icon-text {
        font-size: 8px;
    }
}
</style>
